/**
 * Performance: the live graphs, and the 24-hour maintenance window.
 *
 * Two ranges over the same charts, because the collector never stops. The agent buffers a rolling
 * thirty minutes in memory and answers `StartLiveMetrics` with the whole buffer, so **the graphs are
 * full the moment the page opens** rather than drawing themselves one point at a time - that history
 * reply is the reason this tab does not start empty, and losing it is how a working feature would
 * look broken. Maintenance mode is the other range: the agent persists a sample a minute for
 * twenty-four hours, with this page closed, and that history is fetched on every switch.
 *
 * `onShow` is deliberately absent. The samples land whether or not this tab is showing - the shell
 * subscribes once for the life of the page - and a chart rebuilt on reveal would throw away the
 * history the hub already delivered.
 */
import { insets } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import type { LiveMetrics } from '../../contracts.gen.js';
import { localDateTime } from '../../format.js';
import {
  buildCharts,
  LIVE_WINDOW_MS,
  MAINTENANCE_WINDOW_MS,
  summaryLine
} from '../../metrics.js';
import { ChartCard, type ChartSpec } from '../../ui/ChartCard.js';
import { confirm } from '../../ui/dialogs.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  fillWith,
  FONT,
  GAP,
  h2,
  hstack,
  muted,
  splitRow,
  stackOf,
  text,
  value,
  vstack
} from '../../ui/layout.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

/**
 * How much of the agent's buffer to keep here.
 *
 * The agent's own window is thirty minutes at two-second cadence while watched - about 900 samples -
 * so the cap is generous rather than tight. It exists because nothing else would ever drop a sample:
 * a page left open all afternoon would otherwise grow an array forever, and the window filter would
 * quietly be doing the work of a memory bound.
 */
const MAX_BUFFER = 2_000;

export interface PerformanceTab extends DeviceTab {
  /** A live sample pushed by the agent. */
  applySample(sample: LiveMetrics): void;
  /** The agent's whole rolling buffer, sent in reply to the watch. */
  applyHistory(samples: readonly LiveMetrics[]): void;
  /** The device went offline: the current-value texts stop being true, the history stays. */
  clearLive(): void;
  /** The device record changed - the maintenance deadline is on it. */
  applyDevice(): void;
}

export function performanceTab(dc: DeviceTabContext): PerformanceTab {
  let buffer: LiveMetrics[] = [];
  let maintenance: LiveMetrics[] = [];
  let maintenanceView = false;
  let toggling = false;

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();

  const liveButton = button('Live · 30 min', () => showLive());
  const maintenanceButton = button('Maintenance · 24 h', () => void showMaintenance());
  const rangeCaption = muted('');
  const maintenanceToggle = button('', () => void toggleMaintenance());

  const controls = splitRow({ align: 'center' });
  fillWith(
    controls,
    stackOf(buttonRow(), liveButton, maintenanceButton, rangeCaption)
  );
  controls.controls.add(maintenanceToggle);

  const maintenanceBanner = card({ tint: 'info', bordered: false, padding: insets(GAP.md, GAP.sm) });
  const maintenanceBannerText = text('', { role: 'info', font: FONT.small, wrap: true });
  maintenanceBanner.controls.add(maintenanceBannerText);
  maintenanceBanner.visible = false;

  const waiting = muted(
    "Waiting for data — graphs fill from the agent's rolling history while the device is online."
  );
  const chartHost = vstack({ spacing: GAP.lg, hAlign: 'stretch', autoWidth: false });
  const summary = value('', { wrap: true });

  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      controls,
      maintenanceBanner,
      status.label,
      waiting,
      chartHost,
      summary
    )
  );

  panel.controls.addRange([h2('Live performance'), body]);

  /**
   * One `ChartCard` per chart, reused across rebuilds while the set of charts is unchanged.
   *
   * Rebuilding them every two seconds would be the wrong trade twice over: a `Sparkline` is one
   * control that repaints from its series, so replacing it buys nothing, and the charts arrive in a
   * stable order (CPU, memory, each GPU, temperatures, network, disks) that only moves when the
   * machine's own inventory of sensors does.
   */
  const cards: ChartCard[] = [];

  function render(): void {
    const source = maintenanceView ? maintenance : buffer;
    const specs: ChartSpec[] = buildCharts(
      source,
      maintenanceView ? MAINTENANCE_WINDOW_MS : LIVE_WINDOW_MS
    );

    if (specs.length !== cards.length) {
      cards.length = 0;
      chartHost.controls.clear();
      for (const spec of specs) {
        const chart = new ChartCard(spec);
        cards.push(chart);
        chartHost.controls.add(chart.panel);
      }
    } else {
      specs.forEach((spec, i) => cards[i]!.apply(spec));
    }

    waiting.visible = specs.length === 0;
    // The summary is live-only: it is the newest reading, and on a 24-hour view the newest reading
    // is whatever the machine happened to be doing a minute before somebody opened the page.
    const latest = maintenanceView ? null : (buffer[buffer.length - 1] ?? null);
    summary.text = summaryLine(latest);
    summary.visible = summary.text.length > 0;

    rangeCaption.text = maintenanceView
      ? `Maintenance history — past 24 hours, 1-minute samples (${maintenance.length} stored)`
      : 'Past 30 minutes';

    liveButton.enabled = maintenanceView;
    maintenanceButton.enabled = true;
    dc.relayout();
  }

  function showLive(): void {
    maintenanceView = false;
    render();
  }

  /** Fetched on every switch, so a second click refreshes with the newest stored samples. */
  async function showMaintenance(): Promise<void> {
    status.busy('Loading the stored 24-hour history…');
    try {
      maintenance = [...(await crm.getMaintenanceHistory(dc.deviceId, dc.ctx.dispose.signal()))];
      if (dc.ctx.cancelled()) return;
      status.clear();
      maintenanceView = true;
      render();
    } catch (error) {
      if (!dc.ctx.cancelled()) {
        status.error(describeError(error));
        dc.relayout();
      }
    }
  }

  function maintenanceActive(): boolean {
    const until = dc.device()?.maintenanceUntilUtc;
    // A DEADLINE, not a flag - it expires with nothing running it down, which is why this is a
    // comparison rather than a boolean on the record.
    return !!until && Date.parse(until) > Date.now();
  }

  async function toggleMaintenance(): Promise<void> {
    if (toggling) return;
    const enabling = !maintenanceActive();
    const ok = await confirm(dc.ctx.desktop, {
      title: enabling ? 'Maintenance mode' : 'End maintenance',
      message: enabling
        ? 'Record a sample of every monitor once a minute for the next 24 hours?\n\nIt keeps ' +
          'recording with this page closed, and the stored history is what the 24-hour view reads.'
        : 'End maintenance mode now?\n\nThe device stops recording. What it has already stored stays ' +
          'readable for the rest of its 24-hour window.'
    });
    if (!ok || dc.ctx.cancelled()) return;

    toggling = true;
    maintenanceToggle.enabled = false;
    status.busy(enabling ? 'Turning maintenance mode on…' : 'Turning maintenance mode off…');
    try {
      await crm.setMaintenanceMode(dc.deviceId, enabling);
      if (dc.ctx.cancelled()) return;
      status.clear();
      // Re-read rather than patch: the deadline the server chose is the one that governs, and the
      // header's own banners key off the same record.
      await dc.reload();
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    } finally {
      toggling = false;
      maintenanceToggle.enabled = true;
      applyDevice();
      dc.relayout();
    }
  }

  function applyDevice(): void {
    const active = maintenanceActive();
    maintenanceToggle.text = active ? 'End maintenance' : 'Maintenance mode (24 h)';
    maintenanceBanner.visible = active;
    const until = dc.device()?.maintenanceUntilUtc;
    maintenanceBannerText.text = active
      ? `Maintenance mode — recording 1-minute samples until ${localDateTime(until)}.`
      : '';
  }

  applyDevice();
  render();

  return {
    panel,
    applySample(sample) {
      buffer.push(sample);
      if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);
      if (!maintenanceView) render();
    },
    applyHistory(samples) {
      // Replaces rather than merges: the agent's answer IS its buffer, and merging would double
      // every sample that arrived between the watch and the reply.
      buffer = samples.slice(-MAX_BUFFER);
      if (!maintenanceView) render();
    },
    clearLive() {
      // The charts are kept - history is honest data - but the current-value line is not: an offline
      // device pushes nothing, and leaving the last reading up presents it as live.
      summary.text = '';
      summary.visible = false;
    },
    applyDevice
  };
}
